<template>
	<header
		:class="['marketing-hero', { 'hero--full': fullHeight }]"
		:style="{ 'background-color': hero.backgroundColor ? hero.backgroundColor : defaultBackgroundColor }"
	>
		<div
			:class="[
				'container position-relative',
				{ 'pt-500-md pt-300-smo': !props.isWebview },
				{ 'pb-500-md pb-300-smo': props.hasBottomPadding }
			]"
		>
			<div class="grid-md display-flex flex-direction-column-smo align-items-center">
				<div class="col-6-md marketing-hero--content">
					<slot name="eyebrow"></slot>
					<p
						v-if="hero.heading"
						class="col-12 overline mb-200"
						:class="theme === 'dark' ? 'text-color-gray-40' : 'text-color-current-color'"
					>
						{{ hero.heading }}
					</p>
					<h1
						v-if="hero.title"
						:class="[titleHeadingStyle, 'hero__title', { 'text-color-white': theme === 'dark' }]"
						v-html="hero.title"
					></h1>
					<p
						v-if="hero.description"
						class="line-height-150 col-10-md body-lg"
						:class="{ 'text-color-white': theme === 'dark' }"
						v-html="hero.description"
					></p>
					<slot name="cta"></slot>
				</div>
				<div class="col-6-md">
					<div
						:class="[
							'display-flex align-items-center justify-content-center flex-direction-column-md ml-400-md',
							{ 'mt-200': isMobileNotWebview }
						]"
					>
						<img
							v-reveal:fade-up
							:src="heroImage"
							alt=""
							role="presentation"
							class="width-75-smo rounded"
						/>
					</div>
				</div>
			</div>
		</div>
	</header>
</template>

<script setup lang="ts">
import { appCloudfrontPath } from '@constants';
import { computed } from 'vue';
import { MarketingImageHero } from 'types/layout';
import { useAppStore } from '@stores/app';

interface Props {
	hero: MarketingImageHero;
	theme?: 'dark' | 'light';
	fullHeight?: boolean;
	isWebview?: boolean;
	hasBottomPadding?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
	theme: 'light',
	fullHeight: false,
	isWebview: false,
	hasBottomPadding: true
});

const appStore = useAppStore();

const defaultBackgroundColor = '#151513';

const heroImage = computed(() => {
	return `${appCloudfrontPath}${props.hero.imageUrl}`;
});

const isMobileNotWebview = computed(() => {
	return appStore.isMobile && !props.isWebview;
});

const titleHeadingStyle = computed(() => {
	return props.hero.headingSize ?? 'heading-xl';
});
</script>

<style lang="scss" scoped>
@use '../../styles/utilities/respond-to.scss' as *;

.marketing-hero {
	align-items: center;
	display: flex;
	min-height: 27rem;
	padding-top: 2rem;
	position: relative;

	@include respond-to(md) {
		min-height: 32rem;
		padding-top: 0.5rem;
	}

	&.hero--full {
		min-height: 100vh;
	}

	&--content {
		@include respond-to(smo) {
			display: flex;
			flex-direction: column;
			align-items: center;
			text-align: center;
		}
	}
}

.width-75-smo {
	@include respond-to(smo) {
		width: 75%;
	}
}
</style>
